import { useMutation } from '@tanstack/react-query';
import {
  Clapperboard,
  Link2,
  ListOrdered,
  LoaderCircle,
  MessageSquareWarning,
} from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Input } from '@repo/ui/input';

import { trackEvent } from '../analytics/posthog';
import { contactSupport } from '../api/support';
import { useAuth } from '../auth/use-auth';
import { UserMenu } from '../auth/user-menu';
import { AlertIcon, ArrowLeftIcon, CheckIcon } from '../lib/icons';

const SUPPORT_EMAIL = 'hello@floto.ai';

/** What makes a request quick to answer. Attachments are not supported, so
 *  screenshots travel as links. */
const TIPS = [
  {
    Icon: ListOrdered,
    body: 'The steps you took, and what you expected to happen.',
    title: 'What you were doing',
  },
  {
    Icon: MessageSquareWarning,
    body: 'Any error message, word for word.',
    title: 'What happened instead',
  },
  {
    Icon: Link2,
    body: 'A link to the room, or the page you were testing.',
    title: 'Where it happened',
  },
  {
    Icon: Clapperboard,
    body: 'Links to screenshots or a screen recording (Loom, Drive…).',
    title: 'Something we can see',
  },
];

/** Same shape as the floto app's contact form: required, and an address. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** ApiError ends its message with the status, which is for logs. */
const reasonOf = (err: unknown) =>
  err instanceof Error && err.message
    ? err.message.replace(/ \(\d{3}\)$/, '')
    : 'Failed to send. Please try again later.';

/**
 * "/support" — writing to the Floto team, from the account menu.
 *
 * Laid out like the rest of the product: the board's dotted ground, the way
 * back to every room top-left. The form on the left; on the right, what a
 * useful request includes and what happens after sending — it is email, read
 * and answered by people, so the screen says so rather than dressing it up
 * as a chat.
 */
export function SupportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [name, setName] = useState(user?.displayName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [message, setMessage] = useState('');
  const [invalid, setInvalid] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: contactSupport,
    onSuccess: () => {
      trackEvent('support_contacted', { length: message.trim().length });
    },
  });
  const sent = send.isSuccess ? send.variables : null;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      setInvalid('All fields are required');
      return;
    }
    if (!EMAIL.test(email.trim())) {
      setInvalid('Please provide a valid email address');
      return;
    }
    setInvalid(null);
    send.mutate({
      email: email.trim(),
      message: message.trim(),
      name: name.trim(),
    });
  };

  const startOver = () => {
    setMessage('');
    send.reset();
  };

  const error = invalid ?? (send.isError ? reasonOf(send.error) : null);

  return (
    <main className="relative isolate flex min-h-svh flex-col overflow-hidden bg-warm lg:h-svh">
      {/* The board's own dotted surface. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.07] [background-image:radial-gradient(#1a1a1a_1px,transparent_1px)] [background-size:24px_24px]"
      />

      <header className="flex items-center justify-between px-6 pt-6">
        <button
          className="flex h-11 cursor-pointer items-center gap-2 rounded-button bg-surface pr-4.5 pl-3.5 text-ui font-bold text-text-primary shadow-bar transition hover:bg-surface-hover"
          onClick={() => {
            void navigate('/');
          }}
          type="button"
        >
          <ArrowLeftIcon aria-hidden="true" className="size-4" />
          All Rooms
        </button>
        <UserMenu />
      </header>

      <div className="mx-auto grid min-h-0 w-full max-w-5xl flex-1 items-center lg:overflow-y-auto gap-14 px-6 py-8 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] lg:gap-20 lg:py-4">
        {/* The form. */}
        <section className="duration-700 animate-in fade-in slide-in-from-bottom-3 motion-reduce:animate-none">
          <h1 className="!m-0 !text-new-room-title !font-bold !text-text-primary">
            Get in touch
          </h1>
          <p className="mt-3 text-body leading-relaxed text-text-secondary">
            A bug, a question, an idea for the room — it goes straight to the
            people building Floto.
          </p>

          {sent ? (
            <div
              className="mt-5 rounded-panel border border-hairline bg-surface p-6 shadow-room duration-300 animate-in fade-in zoom-in-95 motion-reduce:animate-none"
              role="status"
            >
              <span className="flex size-10 items-center justify-center rounded-pill bg-highlight">
                <CheckIcon aria-hidden="true" className="size-5 text-ink" />
              </span>
              <p className="mt-4 text-subtitle font-bold text-text-primary">
                Thank you for your message!
              </p>
              <p className="mt-1 text-ui leading-relaxed text-text-secondary">
                We&apos;ll get back to you soon at{' '}
                <span className="font-semibold text-text-primary">
                  {sent.email}
                </span>
                . A copy of what you wrote is on its way there too.
              </p>
              <button
                className="mt-5 h-10 cursor-pointer rounded-button px-3.5 text-ui font-semibold text-text-primary ring-1 ring-hairline transition hover:bg-surface-hover"
                onClick={startOver}
                type="button"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              className="mt-5 space-y-3.5 rounded-panel border border-hairline bg-surface p-5 shadow-room"
              noValidate
              onSubmit={submit}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field id="support-name" label="Name">
                  <Input
                    autoComplete="name"
                    className="h-11 rounded-button border-hairline-alt text-ui-lg"
                    disabled={send.isPending}
                    id="support-name"
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                    placeholder="Your full name"
                    required
                    value={name}
                  />
                </Field>
                <Field id="support-email" label="Email">
                  <Input
                    autoComplete="email"
                    className="h-11 rounded-button border-hairline-alt text-ui-lg"
                    disabled={send.isPending}
                    id="support-email"
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                    placeholder="you@example.com"
                    required
                    type="email"
                    value={email}
                  />
                </Field>
              </div>
              <Field id="support-message" label="Message">
                <textarea
                  className="block min-h-28 w-full resize-none rounded-button border border-hairline-alt bg-transparent px-3 py-2.5 text-ui-lg text-text-primary outline-none transition placeholder:text-text-muted focus-visible:border-ink/30 focus-visible:ring-2 focus-visible:ring-brand/20 disabled:opacity-50"
                  disabled={send.isPending}
                  id="support-message"
                  onChange={(e) => {
                    setMessage(e.target.value);
                  }}
                  placeholder="What were you trying to do, and what happened instead?"
                  required
                  rows={4}
                  value={message}
                />
              </Field>

              {error ? (
                <p
                  className="flex items-center gap-1.5 text-caption font-medium text-critical"
                  role="alert"
                >
                  <AlertIcon aria-hidden="true" className="size-3.5 shrink-0" />
                  {error}
                </p>
              ) : null}

              <button
                className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-button bg-brand text-ui-lg font-semibold text-white transition hover:bg-brand-alt active:scale-[0.98] disabled:cursor-default disabled:opacity-70"
                disabled={send.isPending}
                type="submit"
              >
                {send.isPending ? (
                  <>
                    <LoaderCircle
                      aria-hidden="true"
                      className="size-4 animate-spin motion-reduce:animate-none"
                    />
                    Sending…
                  </>
                ) : (
                  'Send'
                )}
              </button>
            </form>
          )}

          <p className="mt-4 text-caption text-text-muted">
            We typically reply within 24 hours. You can write to us directly at{' '}
            <a
              className="font-semibold text-text-primary underline underline-offset-2 hover:text-brand"
              href={`mailto:${SUPPORT_EMAIL}`}
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </section>

        {/* What makes a request quick to answer, and what happens after
            sending — so the one thing the form cannot say (it is email, read by
            people) is on the screen before anyone writes. */}
        <aside className="relative hidden duration-700 animate-in fade-in slide-in-from-bottom-4 lg:block motion-reduce:animate-none">
          <span
            aria-hidden="true"
            className="absolute -top-6 -right-4 -z-10 size-24 rounded-pill bg-sunny/60"
          />
          <div className="rounded-panel border border-hairline bg-surface p-6 shadow-room">
            <h2 className="!m-0 !text-subtitle !font-bold !text-text-primary">
              Tell us as much as you can
            </h2>
            <p className="mt-1 text-ui text-text-secondary">
              The more we can see, the faster we can fix it.
            </p>
            <ul className="mt-4 space-y-3">
              {TIPS.map(({ Icon, body, title }) => (
                <li className="flex gap-3" key={title}>
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-button bg-warm text-text-control ring-1 ring-hairline">
                    <Icon aria-hidden="true" className="size-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-ui-strong font-semibold text-text-primary">
                      {title}
                    </span>
                    <span className="block text-caption leading-snug text-text-muted">
                      {body}
                    </span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-5 border-t border-hairline pt-4">
              <p className="text-small-label text-text-muted uppercase">
                What happens next
              </p>
              <ol className="mt-3 space-y-2.5">
                {[
                  'It goes straight to the people building Floto.',
                  `A copy lands in ${(sent?.email ?? email).trim() || 'your inbox'}.`,
                  'We reply by email, usually within 24 hours.',
                ].map((step, i) => (
                  <li
                    className="flex items-center gap-3 text-ui text-text-primary"
                    key={step}
                  >
                    <span
                      className={[
                        'flex size-6 shrink-0 items-center justify-center rounded-pill text-tag font-bold',
                        sent
                          ? 'bg-highlight text-ink'
                          : 'bg-warm text-text-control ring-1 ring-hairline',
                      ].join(' ')}
                    >
                      {sent ? (
                        <CheckIcon aria-hidden="true" className="size-3.5" />
                      ) : (
                        i + 1
                      )}
                    </span>
                    <span className="min-w-0 break-words">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

function Field({
  children,
  id,
  label,
}: {
  children: ReactNode;
  id: string;
  label: string;
}) {
  return (
    <div className="text-left">
      <label
        className="mb-1.5 block text-ui-strong font-semibold text-text-primary"
        htmlFor={id}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
